"use client";

import { useCallback, useState } from "react";

import { SignatureInput } from "@/components/ui/signature-input";
import { trpc } from "@/common/utils/trpc";
import { navigateBack } from "@/common/utils/navigateBack";
import { ROUTES } from "@/setup/frontend/routes";
import {
	Alert,
	Box,
	Button,
	Card,
	Checkbox,
	Container,
	Flex,
	Group,
	Loader,
	Modal,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
	IconArrowLeft,
	IconEye,
	IconFileText,
	IconInfoCircle,
} from "@tabler/icons-react";
import { useNavigate, useSearchParams } from "react-router";
import { Link } from "react-router";

export default function CaregiverAppFormDetailPage() {
	const [searchParams] = useSearchParams();
	const careFormTypeId = Number(searchParams.get("careFormTypeId"));
	const clientId = Number(searchParams.get("clientId"));
	const payerSourceId = Number(searchParams.get("payerSourceId"));

	const navigate = useNavigate();
	const [acknowledged, setAcknowledged] = useState(false);
	const [signature, setSignature] = useState<string | null>(null);
	const [previewOpened, previewControls] = useDisclosure(false);

	const {
		data: form,
		isLoading,
		error,
	} = trpc.caregiverForms.getFormDetails.useQuery({
		care_form_type_id: careFormTypeId || undefined,
		client_id: clientId || undefined,
		payer_source_id: payerSourceId || undefined,
	});

	const handleClosePreview = useCallback(() => {
		previewControls.close();
	}, [previewControls]);

	const utils = trpc.useUtils();
	const fillFormMutation = trpc.caregiverForms.caregiverFillForm.useMutation({
		onSuccess: () => {
			notifications.show({
				title: "Form Completed",
				message: "Form has been submitted successfully.",
				color: "green",
			});
			utils.caregiverForms.listMyIncompleteForms.invalidate();
			navigate(ROUTES.CAREGIVER_APP.FORMS.ROOT);
		},
		onError: (err) => {
			notifications.show({
				title: "Error",
				message: err.message || "Failed to submit form",
				color: "red",
			});
		},
	});

	const hasCarePlanAck =
		form?.caregiverFormFields.some((f) => f.type === "care_plan_ack") ?? false;
	const signatureField = form?.caregiverFormFields.find(
		(f) => f.type === "signature",
	);

	const canSubmit =
		(!hasCarePlanAck || acknowledged) && (!signatureField || signature);

	const handleSubmit = () => {
		if (!canSubmit) return;
		fillFormMutation.mutate({
			care_form_type_id: careFormTypeId || undefined,
			client_id: clientId || undefined,
			payer_source_id: payerSourceId || undefined,
			signature: signature ?? "",
		});
	};

	if (isLoading) {
		return (
			<Flex direction="column" h="100vh">
				<Box style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
					<Group h={64} px="md" justify="space-between">
						<Button
							variant="subtle"
							size="compact-md"
							onClick={() =>
								navigateBack(navigate, ROUTES.CAREGIVER_APP.FORMS.ROOT)
							}
						>
							<IconArrowLeft size={20} />
						</Button>
						<Title order={4} style={{ flex: 1, textAlign: "center" }}>
							Form
						</Title>
						<Box w={48} />
					</Group>
				</Box>
				<Flex align="center" justify="center" style={{ flex: 1 }}>
					<Stack align="center" gap="md">
						<Loader size="lg" color="blue" />
						<Text size="lg" c="dimmed" fw={500}>
							Loading form...
						</Text>
					</Stack>
				</Flex>
			</Flex>
		);
	}

	if (!form || error) {
		return (
			<Flex direction="column" h="100vh">
				<Box style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
					<Group h={64} px="md" justify="space-between">
						<Button
							variant="subtle"
							size="compact-md"
							onClick={() =>
								navigateBack(navigate, ROUTES.CAREGIVER_APP.FORMS.ROOT)
							}
						>
							<IconArrowLeft size={20} />
						</Button>
						<Title order={4} style={{ flex: 1, textAlign: "center" }}>
							Form
						</Title>
						<Box w={48} />
					</Group>
				</Box>
				<Container p="md">
					<Card withBorder>
						<Stack align="center" gap="md" p="md">
							<Text size="lg" fw={500}>
								Error Loading Form
							</Text>
							{error && (
								<Text size="sm" c="red">
									{error.message}
								</Text>
							)}
							<Button onClick={() => navigate(ROUTES.CAREGIVER_APP.FORMS.ROOT)}>
								Return to Forms
							</Button>
						</Stack>
					</Card>
				</Container>
			</Flex>
		);
	}

	return (
		<Flex direction="column" flex={1} style={{ overflow: "hidden" }} w="100%">
			<Box style={{ borderBottom: "1px solid var(--mantine-color-gray-3)" }}>
				<Group h={64} px="md" justify="space-between">
					<Button
						variant="subtle"
						size="compact-md"
						onClick={() =>
							navigateBack(navigate, ROUTES.CAREGIVER_APP.FORMS.ROOT)
						}
					>
						<IconArrowLeft size={20} />
					</Button>
					<Title order={4} style={{ flex: 1, textAlign: "center" }}>
						{form.formTypeName}
					</Title>
					<Box w={48} />
				</Group>
			</Box>

			<Box style={{ flex: 1, overflowY: "auto" }}>
				<Container p="md">
					<Stack gap="lg">
						<Card withBorder>
							<Stack gap="sm">
								<Title order={5}>
									{form.client?.firstName} {form.client?.lastName}
								</Title>
								<Text size="sm" c="dimmed">
									{form.formTypeName}
								</Text>
								{form.pdfDataUrl && (
									<Button
										variant="outline"
										leftSection={<IconEye size={16} />}
										onClick={previewControls.open}
										fullWidth
									>
										Preview Form
									</Button>
								)}
							</Stack>
						</Card>

						<Modal
							opened={previewOpened}
							onClose={handleClosePreview}
							title="Form Preview"
							size="xl"
							fullScreen
						>
							<Stack gap="md" style={{ overflow: "auto", flex: 1 }}>
								<Alert
									icon={<IconInfoCircle size={16} />}
									color="blue"
									title="Preview Only"
								>
									This is a preview of the form for your reference. Do not fill
									out the form here.
								</Alert>
								<Flex flex={1} mih="50%">
									<iframe
										src={form.pdfDataUrl ?? undefined}
										style={{
											width: "100%",
											height: "80vh",
											border: "none",
										}}
										title="Form Preview"
									/>
								</Flex>
							</Stack>
						</Modal>

						{form.caregiverFormFields.map((field, idx) => {
							if (field.type === "care_plan_ack") {
								return (
									<div key={idx}>
										{form.carePlanId && (
											<Card withBorder>
												<Stack gap="sm">
													<Group gap="xs">
														<IconFileText size={18} />
														<Text fw={500}>Care Plan</Text>
													</Group>
													<Text size="sm" c="dimmed">
														Review the active care plan for{" "}
														{form.client?.firstName} {form.client?.lastName}{" "}
														before acknowledging this form.
													</Text>
													<Button
														variant="outline"
														component={Link}
														to={ROUTES.CAREGIVER_APP.CARE_PLANS(
															form.carePlanId,
														)}
														leftSection={<IconFileText size={16} />}
														fullWidth
													>
														View Care Plan
													</Button>
												</Stack>
											</Card>
										)}

										{!form.carePlanId && (
											<Alert
												icon={<IconInfoCircle size={16} />}
												color="yellow"
												title="No Care Plan Available"
											>
												There is no active care plan linked for this client.
												Please contact your supervisor as you need to review the
												care plan before completing this form.
											</Alert>
										)}

										{form.carePlanId && (
											<Card withBorder mt="lg">
												<Stack gap="md">
													<Title order={5}>Acknowledgement</Title>
													<Checkbox
														checked={acknowledged}
														onChange={(event) =>
															setAcknowledged(event.currentTarget.checked)
														}
														label="I have received the participant-specific information and am able to apply it during my interactions with the participant."
													/>
												</Stack>
											</Card>
										)}
									</div>
								);
							}
							if (field.type === "signature") {
								return (
									<Card withBorder key={idx}>
										<Stack gap="md">
											<Title order={5}>{field.label}</Title>
											<SignatureInput
												value={signature}
												onChange={setSignature}
												label={field.label}
												required
											/>
										</Stack>
									</Card>
								);
							}
							return null;
						})}

						<Button
							fullWidth
							size="lg"
							disabled={!canSubmit}
							loading={fillFormMutation.isPending}
							onClick={handleSubmit}
						>
							Submit
						</Button>
					</Stack>
				</Container>
			</Box>
		</Flex>
	);
}
